/**
 * SummaryCards — Expenses page financial header (v77.99.102, iter 122.exp1).
 *
 * A banking-app-style summary block that answers "What is my financial
 * position right now?" in seconds. Top of the Expenses page.
 *
 * Hierarchy (highest visual weight first):
 *   1. Conversational headline sentence
 *   2. "You're owed" KPI tile (full-width, dominant — green)
 *   3. Three secondary KPI tiles in a row: Total · You owe · Settled
 *   4. Net settlement breakdown card (only when transfers exist)
 *
 * Colour language (standardised across the app):
 *   • Green  #1F9FA4 / #D6F0EF — settled · you're owed
 *   • Amber  #B88A3A / #FBF0DC — awaiting payment / approval / receipt
 *   • Red    #C0291D / #FBE6E3 — disputed or overdue only
 *   • Grey   #6F828A / #EEF3F4 — voided / archived / informational
 *   • Teal   #1F9FA4              — primary navigation and actions
 *
 * All copy is conversational — never accounting jargon.
 */
import { Button } from "@/components/ui/button";
import { money } from "@/lib/format";
import { ChevronRight } from "lucide-react";

// ---------- helpers ------------------------------------------------------

function buildHeadline(summary, currency, outstandingCount, userId) {
  // Decide tone + sentence based on who's owed what.
  const net = Math.max(0, summary?.net_outstanding ?? summary?.outstanding ?? 0);
  const settlements = summary?.settlements || [];
  const owedToMe = settlements
    .filter((s) => s.to_id === userId)
    .reduce((a, b) => a + (b.amount || 0), 0);
  const owedByMe = settlements
    .filter((s) => s.from_id === userId)
    .reduce((a, b) => a + (b.amount || 0), 0);

  if (net <= 0.005) {
    return {
      tone: "calm",
      sentence: "Everything is settled right now.",
      amount: null,
    };
  }
  if (owedToMe > 0 && owedByMe === 0) {
    return {
      tone: "positive",
      sentence: `You're currently owed ${money(owedToMe, currency)} across ${outstandingCount} outstanding ${outstandingCount === 1 ? "expense" : "expenses"}.`,
      amount: owedToMe,
    };
  }
  if (owedByMe > 0 && owedToMe === 0) {
    return {
      tone: "attention",
      sentence: `You currently owe ${money(owedByMe, currency)} across ${outstandingCount} outstanding ${outstandingCount === 1 ? "expense" : "expenses"}.`,
      amount: owedByMe,
    };
  }
  if (owedToMe > 0 && owedByMe > 0) {
    return {
      tone: "attention",
      sentence: `You're owed ${money(owedToMe, currency)} and owe ${money(owedByMe, currency)} across ${outstandingCount} outstanding ${outstandingCount === 1 ? "expense" : "expenses"}.`,
      amount: owedToMe - owedByMe,
    };
  }
  return {
    tone: "attention",
    sentence: `${money(net, currency)} outstanding across ${outstandingCount} ${outstandingCount === 1 ? "expense" : "expenses"}.`,
    amount: net,
  };
}

const TONE = {
  positive:  { bg: "#D6F0EF", border: "#A7D8DA", text: "#0F5658", dot: "#1F9FA4" },
  attention: { bg: "#FBF0DC", border: "#E2C588", text: "#6F4E1E", dot: "#B88A3A" },
  alert:     { bg: "#FBE6E3", border: "#E8B6AF", text: "#8C1F12", dot: "#C0291D" },
  calm:      { bg: "#EEF3F4", border: "#D9E1E3", text: "#2E3A3F", dot: "#9DB0B5" },
};

// ---------- sub-components -----------------------------------------------

function HeadlineBanner({ headline, testId }) {
  const t = TONE[headline.tone] || TONE.calm;
  return (
    <div
      className="px-4 py-3 rounded-md text-sm flex items-start gap-2.5"
      style={{
        backgroundColor: t.bg,
        color: t.text,
        border: `1px solid ${t.border}`,
      }}
      data-testid={testId}
    >
      <span
        className="h-2 w-2 rounded-full flex-shrink-0 mt-1.5"
        style={{ backgroundColor: t.dot }}
        aria-hidden="true"
      />
      <p className="leading-relaxed flex-1">{headline.sentence}</p>
    </div>
  );
}

function HeroOwedTile({ summary, currency, userId, outstandingCount }) {
  // Headline tile. Shows the "You're owed" or "You owe" figure with
  // dominant visual weight. Falls back to "Settled" calm tile when
  // there is no outstanding balance.
  const settlements = summary?.settlements || [];
  const owedToMe = settlements
    .filter((s) => s.to_id === userId)
    .reduce((a, b) => a + (b.amount || 0), 0);
  const owedByMe = settlements
    .filter((s) => s.from_id === userId)
    .reduce((a, b) => a + (b.amount || 0), 0);

  if (owedToMe <= 0.005 && owedByMe <= 0.005) {
    const t = TONE.positive;
    return (
      <div
        className="border rounded-md p-5"
        style={{ backgroundColor: t.bg, borderColor: t.border }}
        data-testid="expenses-hero-tile"
      >
        <div
          className="text-xs uppercase tracking-[0.1em]"
          style={{ color: t.text }}
        >
          Net position
        </div>
        <div
          className="font-heading text-3xl font-semibold tabular-nums mt-2"
          style={{ color: t.text }}
        >
          Settled
        </div>
        <div className="text-xs mt-1" style={{ color: t.text }}>
          Balances are even across all active expenses.
        </div>
      </div>
    );
  }

  // Pick the dominant direction. If both exist, prefer the larger
  // figure but still show the secondary line.
  const youAreOwedDominant = owedToMe >= owedByMe;
  const dominantTone = youAreOwedDominant ? TONE.positive : TONE.attention;
  const dominantLabel = youAreOwedDominant ? "You're owed" : "You owe";
  const dominantAmount = youAreOwedDominant ? owedToMe : owedByMe;
  const secondaryAmount = youAreOwedDominant ? owedByMe : owedToMe;
  const secondaryLabel = youAreOwedDominant ? "You owe" : "You're owed";

  return (
    <div
      className="border rounded-md p-5"
      style={{
        backgroundColor: dominantTone.bg,
        borderColor: dominantTone.border,
      }}
      data-testid="expenses-hero-tile"
    >
      <div
        className="text-xs uppercase tracking-[0.1em]"
        style={{ color: dominantTone.text }}
      >
        {dominantLabel}
      </div>
      <div
        className="font-heading text-3xl sm:text-4xl font-semibold tabular-nums mt-2 leading-none"
        style={{ color: dominantTone.text }}
        data-testid="expenses-hero-amount"
      >
        {money(dominantAmount, currency)}
      </div>
      <div
        className="text-xs mt-2"
        style={{ color: dominantTone.text }}
      >
        Across {outstandingCount} outstanding {outstandingCount === 1 ? "expense" : "expenses"}.
      </div>
      {secondaryAmount > 0.005 ? (
        <div
          className="mt-3 pt-3 border-t text-xs flex items-center justify-between"
          style={{ borderColor: dominantTone.border, color: dominantTone.text }}
        >
          <span>{secondaryLabel}</span>
          <span className="font-mono-data tabular-nums font-medium">
            {money(secondaryAmount, currency)}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function CompactTile({ label, value, sub, tone = "calm", testId, valueClassName = "" }) {
  const t = TONE[tone] || TONE.calm;
  return (
    <div
      className="bg-white border border-[#D9E1E3] rounded-md p-3.5 relative overflow-hidden"
      data-testid={testId}
    >
      {/* Thin top stripe in the tile's tone — banking-card cue. */}
      <span
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ backgroundColor: t.dot }}
        aria-hidden="true"
      />
      <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mt-1">
        {label}
      </div>
      <div
        className={
          "font-heading text-lg font-semibold tabular-nums mt-1.5 leading-tight " +
          valueClassName
        }
        style={{ color: t.text }}
      >
        {value}
      </div>
      {sub ? (
        <div className="text-[11px] text-[#6F828A] mt-1 leading-snug">
          {sub}
        </div>
      ) : null}
    </div>
  );
}

function SettlementCard({ summary, currency, userId, onSettleAll }) {
  if (!summary?.settlements || summary.settlements.length === 0) return null;
  const multi = summary.settlements.length > 1;
  return (
    <div
      className="border border-[#D9E1E3] bg-white rounded-md p-4 sm:p-5"
      data-testid="settlement-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">
          {multi ? "Net settlement transfers" : "Net settlement"}
        </div>
        {summary.outstanding > 0 ? (
          <Button
            size="sm"
            variant="outline"
            onClick={onSettleAll}
            className="border-[#1F9FA4] text-[#116B6E] hover:bg-[#D6F0EF] hover:text-[#0F5658] rounded-sm h-7"
            data-testid="settle-all-button"
          >
            Mark all as settled
          </Button>
        ) : null}
      </div>
      {!multi ? (() => {
        const s0 = summary.settlements[0];
        const youOwe = s0.from_id === userId;
        const owedToYou = s0.to_id === userId;
        const tone = owedToYou ? TONE.positive : TONE.attention;
        return (
          <div className="mt-3 flex items-baseline gap-2 flex-wrap">
            <span className="font-heading text-xl font-semibold" data-testid="settlement-from">
              {youOwe ? "You" : s0.from_name}
            </span>
            <span className="text-[#6F828A]">{youOwe ? "owe" : "owes"}</span>
            <span className="font-heading text-xl font-semibold" data-testid="settlement-to">
              {owedToYou ? "you" : s0.to_name}
            </span>
            <span
              className="font-heading text-xl font-semibold tabular-nums ml-auto"
              style={{ color: tone.text }}
              data-testid="settlement-amount"
            >
              {money(s0.amount, currency)}
            </span>
          </div>
        );
      })() : (
        <ul
          className="mt-3 divide-y divide-[#E4E4E4]"
          data-testid="settlements-list"
        >
          {summary.settlements.map((s, i) => {
            const youOwe = s.from_id === userId;
            const owedToYou = s.to_id === userId;
            const tone = owedToYou ? TONE.positive : TONE.attention;
            return (
              <li
                key={`${s.from_id}-${s.to_id}-${i}`}
                className="py-2 flex items-baseline gap-2 flex-wrap"
                data-testid={`settlement-row-${i}`}
              >
                <span className="font-heading text-base font-semibold">
                  {youOwe ? "You" : s.from_name}
                </span>
                <span className="text-[#6F828A] text-sm">{youOwe ? "owe" : "owes"}</span>
                <span className="font-heading text-base font-semibold">
                  {owedToYou ? "you" : s.to_name}
                </span>
                <span
                  className="font-heading text-base font-semibold tabular-nums ml-auto"
                  style={{ color: tone.text }}
                >
                  {money(s.amount, currency)}
                </span>
              </li>
            );
          })}
          <li className="pt-2 text-[11px] text-[#6F828A]">
            Each transfer is scoped to the parents linked to the relevant child.
          </li>
        </ul>
      )}
    </div>
  );
}

// ---------- main component -----------------------------------------------

/**
 * Expense summary header.
 *
 * Props:
 *   summary           — /api/expenses/summary payload (or null while loading)
 *   currency          — ISO code (e.g. "EUR")
 *   userId            — current user id
 *   outstandingCount  — count of open/partial entries
 *   onSettleAll       — callback for the "Mark all settled" CTA
 */
export function SummaryCards({
  summary,
  currency,
  userId,
  outstandingCount,
  onSettleAll,
}) {
  if (!summary) {
    return (
      <div className="space-y-3" data-testid="expenses-summary">
        <div className="h-14 bg-[#EEF3F4] rounded-md animate-pulse" />
        <div className="h-32 bg-[#EEF3F4] rounded-md animate-pulse" />
        <div className="grid grid-cols-3 gap-2.5">
          <div className="h-20 bg-[#EEF3F4] rounded-md animate-pulse" />
          <div className="h-20 bg-[#EEF3F4] rounded-md animate-pulse" />
          <div className="h-20 bg-[#EEF3F4] rounded-md animate-pulse" />
        </div>
      </div>
    );
  }
  const headline = buildHeadline(summary, currency, outstandingCount, userId);
  const totalLogged = summary.total || 0;
  const settledAmt = summary.paid || 0;

  return (
    <div className="space-y-3" data-testid="expenses-summary">
      {/* 1. Conversational headline sentence */}
      <HeadlineBanner headline={headline} testId="expenses-headline-banner" />

      {/* 2. Dominant hero tile: "You're owed" / "You owe" / "Settled" */}
      <HeroOwedTile
        summary={summary}
        currency={currency}
        userId={userId}
        outstandingCount={outstandingCount}
      />

      {/* 3. Three compact secondary tiles */}
      <div className="grid grid-cols-3 gap-2.5" data-testid="expenses-kpi-row">
        <CompactTile
          label="Total logged"
          value={money(totalLogged, currency)}
          sub={
            summary.voided_count > 0
              ? `${summary.voided_count} voided excluded`
              : null
          }
          tone="calm"
          testId="expenses-kpi-total"
        />
        <CompactTile
          label="Settled"
          value={money(settledAmt, currency)}
          sub={
            summary.partial > 0
              ? `+ ${money(summary.partial, currency)} partial`
              : null
          }
          tone="positive"
          testId="expenses-kpi-settled"
        />
        <CompactTile
          label="Outstanding"
          value={money(summary.net_outstanding ?? summary.outstanding ?? 0, currency)}
          sub={
            outstandingCount > 0
              ? `${outstandingCount} ${outstandingCount === 1 ? "entry" : "entries"}`
              : "Nothing open"
          }
          tone={
            (summary.net_outstanding ?? summary.outstanding ?? 0) > 0.005
              ? "attention"
              : "calm"
          }
          testId="expenses-kpi-outstanding"
        />
      </div>

      {/* 4. Net settlement breakdown (only when transfers exist) */}
      <SettlementCard
        summary={summary}
        currency={currency}
        userId={userId}
        onSettleAll={onSettleAll}
      />

      {/* Payment credits hint */}
      {(summary.credits_received_total || 0) > 0.005 ? (
        <div
          className="text-[11px] text-[#6F828A] flex items-center gap-1.5"
          data-testid="expenses-credits-hint"
        >
          <ChevronRight className="w-3 h-3" />
          {money(summary.credits_received_total, currency)} received in payments
          {(summary.unallocated_credit || 0) > 0.005 ? (
            <span>
              {" "}· {money(summary.unallocated_credit, currency)} still unallocated
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default SummaryCards;
